import { createSlice } from "@reduxjs/toolkit";
import { adjustID, cleanEffice } from "helpers/efficeHelpers";
import {
  dumpEfficeDescendants,
  editEffice,
  setRoleId,
  updatePresenceState,
  updatePresenceDiffs,
  batchUpdateSubs,
  selectData,
} from "slices/efficeCacheSlice";
import { deleteEfficeAndCleanUp, addNode } from "slices/efficeTreeSlice";
import { resetSocketConnectionDrops } from "slices/generalSlice";
import { spaceEnteredFromAnotherDevice } from "slices/spaceSlice";
import { selectSocket, severSocketConnection } from "./socketSlice";
import { getCurrentSpaceChannel } from "./spaceChannelSlice";
let dispatch = null;
let getState = null;
import("Store").then((module) => {
  dispatch = module.store.dispatch;
  getState = module.store.getState;
});

export const slice = createSlice({
  name: "personalChannel",
  initialState: {
    channel: null,
  },
  reducers: {
    setPersonalChannel: (state, action) => {
      state.channel = action.payload;
    },
  },
});
let personalChannel = null;
window.getPersonalChannel = () => console.log(personalChannel);

export const joinPersonalChannel = (userName) =>
  new Promise((resolve) => {
    window.efs_debug && window.log("Will Join Personal Channel");
    let socket = selectSocket();

    if (socket == null || userName == null) {
      return;
    }

    personalChannel = socket.channel("personal:" + userName, {});
    personalChannel.on("byebye", (msg) => severSocketConnection());
    personalChannel.on(
      "ok",
      (msg) =>
        window.efs_debug &&
        window.log("Personal Channel OK: " + JSON.stringify(msg))
    );
    personalChannel.on("removedFromEffice", ({ id }) =>
      dispatch(removedFromEfficeHandler(adjustID(id.toString())))
    );
    personalChannel.on("changed_role_id", ({ effice_id, role_id }) =>
      dispatch(changedRoleIdHandler(adjustID(effice_id), role_id))
    );
    personalChannel.on("newNode", ({ effice, nonce }) => {
      addNode(cleanEffice(effice), nonce ? adjustID(nonce) : nonce);
    });
    personalChannel.on("editEffice", (effice) =>
      dispatch(editEfficeHandler(cleanEffice(effice)))
    );
    personalChannel.on("deleteEffice", (effice) =>
      dispatch(deleteEfficeHandler(cleanEffice(effice)))
    );
    personalChannel.on("presence_state", (stateObject) => {
      let adjustedStateObject = {};
      for (let pid in stateObject) {
        let adjustPid = adjustID(pid);
        adjustedStateObject[adjustPid] = {};
        for (let effId in stateObject[pid]) {
          adjustedStateObject[adjustPid][adjustID(effId)] =
            stateObject[pid][effId];
        }
      }
      dispatch(presenceStateHandler(adjustedStateObject));
    });
    personalChannel.on("presence_diff", ({ joins, leaves }) => {
      let adjustedJoins = {};
      for (let pid in joins) {
        for (let sid in joins[pid]) {
          adjustedJoins[adjustID(pid)] = {};
          adjustedJoins[adjustID(pid)][adjustID(sid)] = joins[pid][sid];
        }
      }
      let adjustedLeaves = {};
      for (let pid in leaves) {
        for (let sid in leaves[pid]) {
          adjustedLeaves[adjustID(pid)] = {};
          adjustedLeaves[adjustID(pid)][adjustID(sid)] = leaves[pid][sid];
        }
      }
      dispatch(
        presenceDiffHandler({ joins: adjustedJoins, leaves: adjustedLeaves })
      );
    });
    personalChannel.on("leaveOtherSpaces", ({ space_id }) =>
      leaveOtherSpacesHandler(space_id)
    );

    personalChannel
      .join()
      .receive("ok", () => {
        resolve();
        window.efs_debug &&
          window.log("Successfully joined Channel - Personal: " + userName);
        if (getState().general.socketConnectionDrops > 0) {
          dispatch(resetSocketConnectionDrops());
        }
      })
      .receive(
        "error",
        ({ reason }) => window.efs_debug && window.log("failed join", reason)
      )
      .receive(
        "timeout",
        () =>
          window.efs_debug && window.log("Networking issue. Still waiting...")
      );
  });

export const newNodeHandler = (effice, nonce) => async (dispatch, getState) => {
  window.efs_debug && window.log("Got a New Node");
  window.efs_debug && window.log({ effice, nonce });
  addNode(effice, nonce);
};

export const editEfficeHandler = (effice) => async (dispatch, getState) => {
  window.efs_debug && window.log("editEfficeHandler");
  window.efs_debug && window.log(effice);
  if (selectData(getState())[effice.id]) {
    dispatch(editEffice(effice));
  }
};

export const deleteEfficeHandler = (effice) => async (dispatch) => {
  window.efs_debug && window.log("Got a effice deletion signal");
  window.efs_debug && window.log(effice);
  dispatch(deleteEfficeAndCleanUp(effice, true));
};

export const presenceStateHandler = (stateObject) => async (dispatch) => {
  window.efs_debug &&
    window.log("Got a presence state: " + JSON.stringify(stateObject));
  dispatch(updatePresenceState(stateObject));
};

export const presenceDiffHandler =
  ({ joins, leaves }) =>
  async (dispatch) => {
    window.efs_debug &&
      window.log("Got a presence diff: " + JSON.stringify({ joins, leaves }));
    dispatch(updatePresenceDiffs({ joins, leaves }));
  };

export const leaveOtherSpacesHandler = (space_id) => {
  window.efs_debug &&
    window.log("Got a leave other spaces message: " + JSON.stringify(space_id));
  try {
    let channelSpaceId = getCurrentSpaceChannel()?.topic?.slice(6);
    if (channelSpaceId && channelSpaceId !== space_id)
      spaceEnteredFromAnotherDevice();
  } catch (e) {
    window.log("Could not handle leaveOtherSpaces");
    window.log("Error: " + JSON.stringify(e, null, 4));
  }
};

export const removedFromEfficeHandler =
  (effice_id) => async (dispatch, getState) => {
    window.efs_debug && window.log("Got removed from effice.");
    window.efs_debug && window.log(effice_id);
    let effice = selectData(getState())[effice_id];
    if (effice) dispatch(deleteEfficeAndCleanUp(effice, false));
  };

export const changedRoleIdHandler =
  (effice_id, role_id) => async (dispatch) => {
    window.efs_debug && window.log("Got Role Id Change.");
    dispatch(setRoleId({ effId: effice_id, role_id: role_id }));
  };

export const rejoinEfficeChannels = (pids) => async (dispatch) => {
  window.efs_debug && window.log("rewatching effice channels");
  window.efs_debug && window.log(pids);
  if (!(pids.length && pids.length > 0)) {
    return;
  }
  personalChannel
    .push("to_rewatch", { privates: pids })
    .receive("ok", (payload) => {
      window.efs_debug && window.log("phoenix replied to to_rewatch:", payload);
      payload.forEach((element) => {
        element.id = adjustID(element.id);
      });
      dispatch(handleReWatchPayload(payload));
    })
    .receive(
      "error",
      (err) =>
        window.efs_debug && window.log("phoenix errored to_rewatch:", err)
    )
    .receive(
      "timeout",
      () => window.efs_debug && window.log("timed out pushing to_rewatch:")
    );
};

const handleReWatchPayload = (payload) => async (dispatch) => {
  dispatch(batchUpdateSubs(payload));
};

export const leaveEfficeChannels = (pids) => {
  window.efs_debug && window.log("leaving effice channels");
  window.efs_debug && window.log(pids);
  if (!(pids.length && pids.length > 0)) {
    return;
  }

  personalChannel
    .push("to_unwatch", { privates: pids })
    .receive(
      "ok",
      (payload) =>
        window.efs_debug &&
        window.log("phoenix replied to to_unwatch:", payload)
    )
    .receive(
      "error",
      (err) =>
        window.efs_debug && window.log("phoenix errored to_unwatch:", err)
    )
    .receive(
      "timeout",
      () => window.efs_debug && window.log("timed out pushing to_unwatch:")
    );

  let start = Date.now();
  dispatch(dumpEfficeDescendants(pids));
  window.efs_debug &&
    window.log("Dump effice descendants took the following ms");
  window.efs_debug && window.log(Date.now() - start);
};

export const { setPersonalChannel } = slice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state) => state.counter.value)`
export const selectPersonalChannel = () => personalChannel;

export default slice.reducer;
